/**
 * @file TransferItemRow.tsx
 * @description Строка документа «Перемещение» на `<lg` — карточка
 * `DocumentCard` (phase19.23 этап B; единый шаблон документа — phase19.24
 * этап 1, шаг 1).
 *
 * На `≥lg` ту же строку рисует `DocumentItemsTable` из `TransferItemsList`:
 * раньше одна `<li>`-сетка отвечала за обе ширины, и таблица перемещения не
 * совпадала ни с приёмкой, ни со сборкой (замечание пользователя по
 * MV-2026-000002).
 *
 * Живые контролы «Куда» и «Кол-во» общие с таблицей — `TransferItemCells`.
 *
 * Подписи «Откуда:» и «Куда:» в карточке обязательны: в таблице их держит
 * шапка колонки «Откуда → Куда», а на телефоне шапки нет, и два адреса подряд
 * читались как «непонятно что» (замечание ревью 2026-09-20). Количество —
 * в слоте `qty` карточки, рядом с названием, как у приёмки и списания.
 *
 * Приёмка qa4 п. 8: у проведённого документа «Куда» — обычной строкой
 * «Куда: A-01-02» под «Откуда», как факт; поле выбора ячейки — только у
 * черновика, и без повтора «откуда →» над ним (оно уже строкой выше).
 */

import { Trash2 } from "lucide-react";

import { TransferQtyCell, TransferTargetCell } from "./TransferItemCells";
import { transferLotLabel, transferSourceLabel, transferTargetText } from "./transfer-row-text";
import { DocumentCard } from "../../ui/DocumentCard";
import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import { WmsSkuThumb } from "../WmsSkuThumb";
import type { StorageBinDto } from "../../../lib/api-bins";
import type {
  TransferItemDto,
  UpdateTransferItemPayload,
} from "../../../lib/api-transfers";
import { formatSkuName } from "../../../lib/api-wms";
import { isIntraWarehouseRow } from "../../../lib/transfer-rows";

interface Props {
  item: TransferItemDto;
  /** Строка, к которой увёл «Подбор» — подсветка. */
  highlighted?: boolean;
  /** Номер строки (1-based) — им же ссылается подпись «Заполните «Куда»». */
  index: number;
  fromWarehouseId: string;
  /** Ячейки склада-источника; пусто — адресного хранения нет. */
  bins: StorageBinDto[];
  editable: boolean;
  busy: boolean;
  onUpdate: (itemId: string, payload: UpdateTransferItemPayload) => void;
  onRemove: (itemId: string) => void;
}

export function TransferItemRow({
  item,
  highlighted = false,
  index,
  fromWarehouseId,
  bins,
  editable,
  busy,
  onUpdate,
  onRemove,
}: Props) {
  const actions: RowAction[] = editable
    ? [
        {
          key: "remove",
          label: "Удалить строку",
          icon: Trash2,
          danger: true,
          disabled: busy,
          run: () => onRemove(item.id),
        },
      ]
    : [];

  // Внутри склада ячейка назначения обязательна — пустая подсвечивается.
  const targetMissing = isIntraWarehouseRow(item, fromWarehouseId) && !item.toBinId;

  return (
    <DocumentCard
      tone={highlighted ? "selected" : "default"}
      media={<WmsSkuThumb />}
      title={
        <>
          <span className="text-on-surface-variant">{index}. </span>
          {formatSkuName(item.sku)}
        </>
      }
      code={item.skuCode}
      qty={
        <TransferQtyCell
          item={item}
          fromWarehouseId={fromWarehouseId}
          editable={editable}
          busy={busy}
          onUpdate={onUpdate}
        />
      }
      meta={[
        transferLotLabel(item),
        `Откуда: ${transferSourceLabel(item)}`,
        // Проведённый документ: «Куда» — фактом, одной строкой с подписью.
        editable ? null : (
          <span key="to" className={targetMissing ? "text-warning" : undefined}>
            Куда: <span className={targetMissing ? undefined : "text-on-surface"}>{transferTargetText(item, fromWarehouseId)}</span>
          </span>
        ),
      ]}
      actions={
        editable ? (
          <>
            <span className="label-editorial shrink-0 self-center">Куда:</span>
            <div className="min-w-0 flex-1">
              <TransferTargetCell
                item={item}
                fromWarehouseId={fromWarehouseId}
                bins={bins}
                editable={editable}
                busy={busy}
                onUpdate={onUpdate}
                showFrom={false}
              />
            </div>
            <RowActionsMenu
              actions={actions}
              title={`Действия со строкой ${index}`}
              ariaLabel={`Действия со строкой ${index}`}
              disabled={busy}
              size="compact"
            />
          </>
        ) : undefined
      }
    />
  );
}
